// API client - connects to real backend
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth token management
// NOTE on storage: we use localStorage because this SPA has no server-side
// session layer that can issue HttpOnly cookies. XSS is mitigated via strict
// CSP + React's automatic escaping (backend sets Content-Security-Policy in
// server.py). Tokens are short-lived (24h) and scoped to the admin dashboard.
const TOKEN_KEY = "ludovic_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const authHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Auto-clear the admin token if the backend ever rejects it (401/403),
// so the user is forced back to the login screen instead of being stuck.
axios.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const sentAuth = !!error?.config?.headers?.Authorization;
    if ((status === 401 || status === 403) && sentAuth) {
      clearToken();
    }
    return Promise.reject(error);
  }
);

// ============== AUTH ==============
export const adminLogin = async (password) => {
  const { data } = await axios.post(`${API}/admin/login`, { password });
  setToken(data.token);
  return data;
};

export const adminVerify = async () => {
  try {
    await axios.get(`${API}/admin/verify`, { headers: authHeaders() });
    return true;
  } catch {
    clearToken();
    return false;
  }
};

export const changeAdminPassword = async (oldPassword, newPassword) => {
  const { data } = await axios.post(
    `${API}/admin/change-password`,
    { old_password: oldPassword, new_password: newPassword },
    { headers: authHeaders() }
  );
  return data;
};

// ============== BOOKINGS ==============
export const createBooking = async (booking) => {
  const { data } = await axios.post(`${API}/bookings`, booking);
  return data;
};

export const computeTravelFee = async (city, address = "") => {
  const { data } = await axios.post(`${API}/travel-fee`, { city, address });
  return data;
};

export const listBookings = async () => {
  const { data } = await axios.get(`${API}/bookings`, { headers: authHeaders() });
  return data;
};

export const getBookedDates = async () => {
  const { data } = await axios.get(`${API}/bookings/booked-dates`);
  return data;
};

export const getBookedSlots = async () => {
  const { data } = await axios.get(`${API}/bookings/booked-slots`);
  return data;
};

export const updateBookingStatus = async (id, status) => {
  const { data } = await axios.patch(`${API}/bookings/${id}`, { status }, { headers: authHeaders() });
  return data;
};

export const deleteBooking = async (id) => {
  await axios.delete(`${API}/bookings/${id}`, { headers: authHeaders() });
};

// ============== GIFT CARDS ==============
export const createGiftCard = async (card) => {
  const { data } = await axios.post(`${API}/gift-cards`, card);
  return data;
};

export const listGiftCards = async () => {
  const { data } = await axios.get(`${API}/gift-cards`, { headers: authHeaders() });
  return data;
};

export const verifyGiftCard = async (code) => {
  const { data } = await axios.post(`${API}/gift-cards/verify`, { code });
  return data;
};

export const updateGiftCard = async (id, used) => {
  await axios.patch(`${API}/gift-cards/${id}`, { used }, { headers: authHeaders() });
};

export const updateGiftCardStatus = async (id, status) => {
  await axios.patch(`${API}/gift-cards/${id}/status`, { status }, { headers: authHeaders() });
};

export const deleteGiftCard = async (id) => {
  await axios.delete(`${API}/gift-cards/${id}`, { headers: authHeaders() });
};

// ============== BLOCKED DATES ==============
export const getBlockedDates = async () => {
  const { data } = await axios.get(`${API}/blocked-dates`);
  return data;
};

export const addBlockedDate = async (date) => {
  await axios.post(`${API}/blocked-dates`, { date }, { headers: authHeaders() });
};

export const addBlockedDatesBulk = async (dates) => {
  await axios.post(`${API}/blocked-dates/bulk`, { dates }, { headers: authHeaders() });
};

export const removeBlockedDate = async (date) => {
  await axios.delete(`${API}/blocked-dates/${date}`, { headers: authHeaders() });
};

// Blocked time slots (specific hours)
export const getBlockedSlots = async () => {
  const { data } = await axios.get(`${API}/blocked-slots`);
  return data;
};

export const addBlockedSlot = async (date, time) => {
  await axios.post(`${API}/blocked-slots`, { date, time }, { headers: authHeaders() });
};

export const removeBlockedSlot = async (date, time) => {
  await axios.delete(`${API}/blocked-slots`, { params: { date, time }, headers: authHeaders() });
};

// ============== PHOTOS ==============
export const listPhotos = async (category = null) => {
  const params = category ? { category } : {};
  const { data } = await axios.get(`${API}/photos`, { params });
  return data;
};

export const uploadPhoto = async (file, category) => {
  const form = new FormData();
  form.append("file", file);
  form.append("category", category);
  const { data } = await axios.post(`${API}/photos`, form, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" }
  });
  return data;
};

export const deletePhoto = async (id) => {
  await axios.delete(`${API}/photos/${id}`, { headers: authHeaders() });
};

export const listGalleryCategories = async () => {
  const { data } = await axios.get(`${API}/gallery-categories`);
  return data;
};
export const createGalleryCategory = async (name) => {
  const { data } = await axios.post(`${API}/gallery-categories`, { name }, { headers: authHeaders() });
  return data;
};
export const deleteGalleryCategory = async (id) => {
  await axios.delete(`${API}/gallery-categories/${id}`, { headers: authHeaders() });
};

// Helper: build full image URL (for photos served from backend)
export const photoUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http") || path.startsWith("/gallery/")) return path;
  return `${BACKEND_URL}${path}`;
};

// ============== MESSAGES ==============
export const sendMessage = async (msg) => {
  const { data } = await axios.post(`${API}/messages`, msg);
  return data;
};

export const listMessages = async () => {
  const { data } = await axios.get(`${API}/messages`, { headers: authHeaders() });
  return data;
};

export const deleteMessage = async (id) => {
  await axios.delete(`${API}/messages/${id}`, { headers: authHeaders() });
};
